import { painelApi } from '@/api/painel';
import { LineChart } from '@/components/painel/charts/LineChart';
import { Sparkline } from '@/components/painel/charts/Sparkline';
import { ErrorBox, Kpi, SectionTitle } from '@/components/painel/Kpi';
import { useLoad } from '@/components/painel/PainelContext';
import { fmtBrl, fmtInt, fmtKwh, fmtKwhNum, fmtMonth, fmtPct } from '@/components/painel/format';

function Delta({ v }: { v: number | null }) {
  if (v == null) return <span className="text-bow-royal/40">—</span>;
  const up = v >= 0;
  return (
    <span className="font-semibold text-bow-royal">
      <span aria-hidden className={up ? 'text-bow-green' : 'text-bow-red'}>
        {up ? '▲' : '▼'}
      </span>{' '}
      {`${up ? '+' : ''}${v.toFixed(1).replace('.', ',')}%`}
    </span>
  );
}

/** Rede Bow consolidada (investidor da empresa): 12 meses, sem dado de motorista. */
export default function PainelRede() {
  const r = useLoad(() => painelApi.rede(), []);
  const meses = r.data?.meses ?? [];
  const atual = meses[meses.length - 1];
  const fechado = meses[meses.length - 2];

  return (
    <div className="pt-1">
      <h2 className="m-0 text-[20px] font-semibold tracking-[-0.02em]">Rede Bow</h2>
      <p className="m-0 mt-1 text-[12.5px] text-bow-royal/55">
        Últimos 12 meses, todas as estações. O mês corrente está em andamento.
      </p>
      {r.error && !r.data && <ErrorBox message={r.error} onRetry={r.reload} />}
      {fechado && atual && (
        <div className="mt-4 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          <Kpi
            label={`Energia · ${fmtMonth(fechado.mes)}`}
            value={fmtKwhNum(fechado.kwh)}
            unit="kWh"
            note={
              <>
                <Delta v={fechado.var_kwh_pct} /> vs mês anterior
                <span className="mt-2 block">
                  <Sparkline values={meses.map((m) => m.kwh)} label="kWh por mês" />
                </span>
              </>
            }
          />
          <Kpi
            label={`Receita · ${fmtMonth(fechado.mes)}`}
            value={fmtBrl(fechado.receita_brl)}
            note={
              <>
                <Delta v={fechado.var_receita_pct} /> vs mês anterior
                <span className="mt-2 block">
                  <Sparkline values={meses.map((m) => Number(m.receita_brl))} label="Receita por mês" />
                </span>
              </>
            }
          />
          <Kpi label={`Motoristas ativos · ${fmtMonth(fechado.mes)}`} value={fmtInt(fechado.motoristas_ativos)} />
          <Kpi label={`Estações com recarga · ${fmtMonth(fechado.mes)}`} value={fmtInt(fechado.estacoes)} />
        </div>
      )}

      {meses.length > 0 && (
        <>
          <SectionTitle>Energia entregue por mês</SectionTitle>
          <div className="rounded-3xl bg-white p-4 shadow-soft">
            <LineChart
              ariaLabel="kWh"
              labels={meses.map((m) => m.mes)}
              values={meses.map((m) => m.kwh)}
              color="#0090E2"
              formatLabel={fmtMonth}
              formatValue={fmtKwh}
            />
          </div>

          <SectionTitle>Mês a mês</SectionTitle>
          <div className="overflow-x-auto rounded-3xl bg-white shadow-soft">
            <table className="w-full min-w-[640px] border-collapse text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.05em] text-bow-royal/55">
                  <th className="px-4 py-3 font-semibold">Mês</th>
                  <th className="px-3 py-3 text-right font-semibold">kWh</th>
                  <th className="px-3 py-3 text-right font-semibold">Receita</th>
                  <th className="px-3 py-3 text-right font-semibold">Recargas</th>
                  <th className="px-3 py-3 text-right font-semibold">Motoristas</th>
                  <th className="px-3 py-3 text-right font-semibold">Estações</th>
                  <th className="px-4 py-3 text-right font-semibold">Ocupação</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {[...meses].reverse().map((m) => (
                  <tr key={m.mes} className="border-t border-bow-royal/[0.07]">
                    <td className="px-4 py-2.5">{fmtMonth(m.mes)}</td>
                    <td className="px-3 py-2.5 text-right">{fmtKwhNum(m.kwh)}</td>
                    <td className="px-3 py-2.5 text-right">{fmtBrl(m.receita_brl)}</td>
                    <td className="px-3 py-2.5 text-right">{fmtInt(m.recargas)}</td>
                    <td className="px-3 py-2.5 text-right">{fmtInt(m.motoristas_ativos)}</td>
                    <td className="px-3 py-2.5 text-right">{fmtInt(m.estacoes)}</td>
                    <td className="px-4 py-2.5 text-right">{fmtPct(m.ocupacao_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="m-0 mt-3 text-[11.5px] leading-snug text-bow-royal/50">
            Receita = app Bow (realizada) + roaming (estimada pela tarifa de operador). Ocupação considera os
            carregadores que tiveram recarga no mês.
          </p>
        </>
      )}
    </div>
  );
}
